import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import Icon from './Icon';

interface AvatarViewerModalProps {
  firstName: string;
  lastName?: string;
  /** Resolved avatar URL (see resolveAvatarSrc). */
  src: string;
  onClose: () => void;
}

/**
 * Shows a profile picture at a size somebody can actually look at.
 *
 * Everywhere else in the app an avatar is a 24-56px circle, which is enough to
 * identify a person and not enough to check whether the photo you just took is
 * one you want on your record. This renders the same image uncropped, so the
 * parts a circular mask hides are visible.
 *
 * Built on Modal rather than a bespoke lightbox so Escape-to-close and
 * backdrop-click come from the same place as every other dialog.
 */
export default function AvatarViewerModal({ firstName, lastName, src, onClose }: AvatarViewerModalProps) {
  const [failed, setFailed] = useState(false);
  const fullName = `${firstName} ${lastName ?? ''}`.trim();

  return (
    <Modal
      title="Profile picture"
      onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>Close</Button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        {failed ? (
          // A signed SK Drive URL that has expired must not render as a broken
          // image icon — same reasoning as the initials fallback in Avatar.
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ink-3)' }}>
            <Icon name="user" size={40} color="var(--border-2)" />
            <p style={{ marginTop: 12, fontSize: 'var(--fs-sm)' }}>
              This picture could not be loaded. Try reopening the page.
            </p>
          </div>
        ) : (
          <img
            src={src}
            alt={fullName ? `${fullName}'s profile picture` : 'Profile picture'}
            onError={() => setFailed(true)}
            style={{
              maxWidth: '100%',
              maxHeight: '60vh',
              borderRadius: 'var(--r-4)',
              // contain, not cover: this view exists to show the whole photo,
              // including whatever the circular crop leaves out.
              objectFit: 'contain',
              background: 'var(--bg)',
            }}
          />
        )}
        {fullName && !failed && (
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{fullName}</p>
        )}
      </div>
    </Modal>
  );
}
